import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ArrowUp,
  ArrowDown,
  ChevronLeft,
  ChevronRight,
  BarChart3,
  Calendar,
  Filter,
  Download,
  FileSpreadsheet,
  FileText,
  Search,
  ClipboardList,
} from 'lucide-react';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { API_CONFIG } from '../config/api';

interface SummaryRow {
  date: string;
  total_pulled: number;
  qualified: number;
  processed: number;
  pct_processed_qualified: number;
  pct_processed_total: number;
}

interface SummaryResponse {
  rows: SummaryRow[];
  total_days: number;
  limit: number;
  offset: number;
}

interface ManualRow {
  case_number: string;
  reason: string;
  comment: string;
  processed_by: string;
  created_at: string;
}

interface ReasonSummary {
  reason: string;
  count: number;
  percentage: number;
}

interface ManualResponse {
  rows: ManualRow[];
  total_rows: number;
  limit: number;
  offset: number;
  reason_summary: ReasonSummary[];
}

type ReportTab = 'executive_summary' | 'manual_reasons';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 0]; // 0 = All

const VALID_TABS: ReportTab[] = ['executive_summary', 'manual_reasons'];

const ExecutiveSummary: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = useMemo<ReportTab>(() => {
    const param = searchParams.get('tab') as ReportTab | null;
    return param && VALID_TABS.includes(param) ? param : 'executive_summary';
  }, [searchParams]);

  const setActiveTab = useCallback(
    (tab: ReportTab) => {
      setSearchParams(tab === 'executive_summary' ? {} : { tab }, { replace: true });
    },
    [setSearchParams]
  );
  const [data, setData] = useState<SummaryRow[]>([]);
  const [totalDays, setTotalDays] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showDetailExportMenu, setShowDetailExportMenu] = useState(false);
  const [isExportingDetails, setIsExportingDetails] = useState(false);

  const totalPages = pageSize === 0 ? 1 : Math.ceil(totalDays / pageSize);

  // Manual Reasons tab state
  const [manualData, setManualData] = useState<ManualRow[]>([]);
  const [manualTotalRows, setManualTotalRows] = useState(0);
  const [reasonSummary, setReasonSummary] = useState<ReasonSummary[]>([]);
  const [manualLoading, setManualLoading] = useState(true);
  const [manualError, setManualError] = useState<string | null>(null);
  const [manualStartDate, setManualStartDate] = useState('');
  const [manualEndDate, setManualEndDate] = useState('');
  const [manualSearch, setManualSearch] = useState('');
  const [manualSortOrder, setManualSortOrder] = useState<'asc' | 'desc'>('desc');
  const [manualCurrentPage, setManualCurrentPage] = useState(0);
  const [manualPageSize, setManualPageSize] = useState(25);
  const [manualShowExportMenu, setManualShowExportMenu] = useState(false);

  const manualTotalPages =
    manualPageSize === 0 ? 1 : Math.ceil(manualTotalRows / manualPageSize);

  useEffect(() => {
    document.title = 'Reports';
    return () => {
      document.title = 'Payroll Agent Dashboard';
    };
  }, []);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('limit', String(pageSize));
      params.set('offset', pageSize === 0 ? '0' : String(currentPage * pageSize));
      params.set('sort_order', sortOrder);
      if (startDate) params.set('start_date', startDate);
      if (endDate) params.set('end_date', endDate);

      const response = await fetch(
        `${API_CONFIG.BASE_URL}/api/executive-summary?${params.toString()}`,
        { credentials: 'include' }
      );

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Session expired. Please refresh the page.');
        }
        throw new Error(`Failed to load data (HTTP ${response.status})`);
      }

      const json: SummaryResponse = await response.json();
      setData(json.rows);
      setTotalDays(json.total_days);
    } catch (err: any) {
      setError(err.message || 'Failed to load executive summary');
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, sortOrder, startDate, endDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchManualData = useCallback(async () => {
    setManualLoading(true);
    setManualError(null);
    try {
      const params = new URLSearchParams();
      params.set('limit', String(manualPageSize));
      params.set(
        'offset',
        manualPageSize === 0 ? '0' : String(manualCurrentPage * manualPageSize)
      );
      params.set('sort_order', manualSortOrder);
      if (manualStartDate) params.set('start_date', manualStartDate);
      if (manualEndDate) params.set('end_date', manualEndDate);
      if (manualSearch.trim()) params.set('search', manualSearch.trim());

      const response = await fetch(
        `${API_CONFIG.BASE_URL}/api/manual-processing-report?${params.toString()}`,
        { credentials: 'include' }
      );

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Session expired. Please refresh the page.');
        }
        throw new Error(`Failed to load data (HTTP ${response.status})`);
      }

      const json: ManualResponse = await response.json();
      setManualData(json.rows);
      setManualTotalRows(json.total_rows);
      setReasonSummary(json.reason_summary);
    } catch (err: any) {
      setManualError(err.message || 'Failed to load manual processing report');
    } finally {
      setManualLoading(false);
    }
  }, [
    manualCurrentPage,
    manualPageSize,
    manualSortOrder,
    manualStartDate,
    manualEndDate,
    manualSearch,
  ]);

  useEffect(() => {
    if (activeTab === 'manual_reasons') {
      fetchManualData();
    }
  }, [activeTab, fetchManualData]);

  const toggleSort = () => {
    setSortOrder((prev) => (prev === 'desc' ? 'asc' : 'desc'));
    setCurrentPage(0);
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(0);
  };

  const clearFilters = () => {
    setStartDate('');
    setEndDate('');
    setCurrentPage(0);
  };

  const manualToggleSort = () => {
    setManualSortOrder((prev) => (prev === 'desc' ? 'asc' : 'desc'));
    setManualCurrentPage(0);
  };

  const manualHandlePageSizeChange = (newSize: number) => {
    setManualPageSize(newSize);
    setManualCurrentPage(0);
  };

  const manualClearFilters = () => {
    setManualStartDate('');
    setManualEndDate('');
    setManualSearch('');
    setManualCurrentPage(0);
  };

  const manualShowingFrom =
    manualPageSize === 0 ? 1 : manualCurrentPage * manualPageSize + 1;
  const manualShowingTo =
    manualPageSize === 0
      ? manualTotalRows
      : Math.min((manualCurrentPage + 1) * manualPageSize, manualTotalRows);

  const exportManualCSV = () => {
    const headers = ['Case Number', 'Reason', 'Comment', 'Processed By', 'Date'];
    const csvRows = [
      headers.join(','),
      ...manualData.map((r) =>
        [
          r.case_number,
          `"${r.reason}"`,
          `"${(r.comment || '').replace(/"/g, '""')}"`,
          `"${r.processed_by}"`,
          r.created_at ? formatDate(r.created_at) : '',
        ].join(',')
      ),
    ];
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `manual_processing_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
    setManualShowExportMenu(false);
  };

  const exportManualExcel = async () => {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('Manual Processing');
    ws.addRow(['Case Number', 'Reason', 'Comment', 'Processed By', 'Date']);
    manualData.forEach((r) =>
      ws.addRow([
        r.case_number,
        r.reason,
        r.comment || '',
        r.processed_by,
        r.created_at ? formatDate(r.created_at) : '',
      ])
    );
    ws.getRow(1).font = { bold: true };
    ws.columns.forEach((col) => {
      col.width = 20;
    });
    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `manual_processing_${new Date().toISOString().split('T')[0]}.xlsx`;
    link.click();
    URL.revokeObjectURL(link.href);
    setManualShowExportMenu(false);
  };

  const exportManualPDF = () => {
    const doc = new jsPDF({ orientation: 'landscape' });
    doc.setFontSize(16);
    doc.text('Manual Processing Report', 14, 20);
    doc.setFontSize(10);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 28);

    autoTable(doc, {
      startY: 35,
      head: [['Case Number', 'Reason', 'Comment', 'Processed By', 'Date']],
      body: manualData.map((r) => [
        r.case_number,
        r.reason,
        r.comment || '',
        r.processed_by,
        r.created_at ? formatDate(r.created_at) : '',
      ]),
      headStyles: { fillColor: [79, 70, 229], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [248, 248, 252] },
    });

    doc.save(`manual_processing_${new Date().toISOString().split('T')[0]}.pdf`);
    setManualShowExportMenu(false);
  };

  useEffect(() => {
    if (!manualShowExportMenu) return;
    const handleClick = () => setManualShowExportMenu(false);
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [manualShowExportMenu]);

  const getReasonColor = (reason: string): string => {
    const r = reason.toLowerCase();
    if (r.includes('document') || r.includes('unclear') || r.includes('unreadable'))
      return 'bg-amber-100 text-amber-800 border-amber-200';
    if (r.includes('complex')) return 'bg-blue-100 text-blue-800 border-blue-200';
    if (r.includes('zero') || r.includes('employee'))
      return 'bg-red-100 text-red-800 border-red-200';
    if (r.includes('technical') || r.includes('error'))
      return 'bg-purple-100 text-purple-800 border-purple-200';
    return 'bg-neutral-100 text-neutral-700 border-neutral-200';
  };

  const formatProcessedBy = (email: string): string => {
    if (!email) return '—';
    const atIdx = email.indexOf('@');
    return atIdx > 0 ? email.substring(0, atIdx) : email;
  };

  const formatDate = (iso: string) => {
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatPct = (value: number) => {
    if (value === 0) return '0%';
    return `${value.toFixed(1)}%`;
  };

  const getPctColor = (value: number) => {
    if (value >= 80) return 'text-green-700 bg-green-100 border border-green-200';
    if (value >= 50) return 'text-amber-700 bg-amber-100 border border-amber-200';
    if (value > 0) return 'text-red-700 bg-red-100 border border-red-200';
    return 'text-neutral-500 bg-neutral-100 border border-neutral-200';
  };

  // --- Export helpers ---

  const getExportHeaders = () => [
    'Date',
    'Total Pulled',
    'Qualified',
    'Processed',
    '% Processed / Qualified',
    '% Processed / Total',
  ];

  const getExportRows = () =>
    data.map((row) => [
      formatDate(row.date),
      row.total_pulled,
      row.qualified,
      row.processed,
      formatPct(row.pct_processed_qualified),
      formatPct(row.pct_processed_total),
    ]);

  const exportCSV = () => {
    const headers = getExportHeaders();
    const rows = getExportRows();
    const csvContent = [
      headers.join(','),
      ...rows.map((r) => r.map((cell) => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `executive_summary_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    setShowExportMenu(false);
  };

  const exportExcel = async () => {
    const headers = getExportHeaders();
    const rows = getExportRows();

    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('Executive Summary');

    ws.addRow(headers);
    const headerRow = ws.getRow(1);
    headerRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF4F46E5' },
    };
    headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    headerRow.alignment = { horizontal: 'center' };

    rows.forEach((r) => ws.addRow(r));

    ws.columns.forEach((col, i) => {
      const maxLen = Math.max(
        headers[i].length,
        ...rows.map((r) => String(r[i]).length)
      );
      col.width = maxLen + 4;
      col.alignment = { horizontal: 'center' };
    });

    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `executive_summary_${new Date().toISOString().split('T')[0]}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
    setShowExportMenu(false);
  };

  const exportPDF = () => {
    const doc = new jsPDF({ orientation: 'landscape' });
    doc.setFontSize(16);
    doc.text('Executive Summary', 14, 18);
    doc.setFontSize(9);
    doc.setTextColor(120);
    const filterText = [startDate && `From: ${startDate}`, endDate && `To: ${endDate}`]
      .filter(Boolean)
      .join('  |  ');
    doc.text(filterText || 'All dates', 14, 25);

    autoTable(doc, {
      startY: 32,
      head: [getExportHeaders()],
      body: getExportRows(),
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [79, 70, 229], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [248, 248, 252] },
      columnStyles: {
        0: { halign: 'center' },
        1: { halign: 'center' },
        2: { halign: 'center' },
        3: { halign: 'center' },
        4: { halign: 'center' },
        5: { halign: 'center' },
      },
    });

    doc.save(`executive_summary_${new Date().toISOString().split('T')[0]}.pdf`);
    setShowExportMenu(false);
  };

  // Close export menu when clicking outside
  useEffect(() => {
    if (!showExportMenu) return;
    const handleClick = () => setShowExportMenu(false);
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [showExportMenu]);

  useEffect(() => {
    if (!showDetailExportMenu) return;
    const handleClick = () => setShowDetailExportMenu(false);
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [showDetailExportMenu]);

  const fetchDetailRows = async () => {
    const params = new URLSearchParams();
    params.set('sort_order', sortOrder);
    if (startDate) params.set('start_date', startDate);
    if (endDate) params.set('end_date', endDate);

    const response = await fetch(
      `${API_CONFIG.BASE_URL}/api/executive-summary/details?${params.toString()}`,
      { credentials: 'include' }
    );
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const json = await response.json();
    return json.rows as Array<{
      case_number: string;
      case_date: string | null;
      imported_date: string | null;
      customer_name: string | null;
      case_title: string | null;
      case_type: string;
      assigned_to: string | null;
      processed: boolean;
      processed_date: string | null;
      processed_by: string | null;
      blocked: boolean;
      blocked_reason: string | null;
    }>;
  };

  const detailHeaders = [
    'Case Number',
    'Case Date',
    'Imported Date',
    'Customer',
    'Title',
    'Case Type',
    'Assigned To',
    'Processed',
    'Processed Date',
    'Processed By',
    'Blocked',
    'Blocked Reason',
  ];

  const detailRowToArray = (r: any) => [
    r.case_number || '',
    r.case_date ? formatDate(r.case_date) : '',
    r.imported_date ? formatDate(r.imported_date) : '',
    r.customer_name || '',
    r.case_title || '',
    r.case_type || '',
    r.assigned_to || '',
    r.processed ? 'Yes' : 'No',
    r.processed_date ? formatDate(r.processed_date) : '',
    r.processed_by || '',
    r.blocked ? 'Yes' : 'No',
    r.blocked_reason || '',
  ];

  const exportDetailCSV = async () => {
    setIsExportingDetails(true);
    try {
      const rows = await fetchDetailRows();
      const csvContent = [
        detailHeaders.join(','),
        ...rows.map((r) =>
          detailRowToArray(r)
            .map((cell) => `"${String(cell).replace(/"/g, '""')}"`)
            .join(',')
        ),
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `executive_summary_details_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      alert(`Export failed: ${err.message}`);
    } finally {
      setIsExportingDetails(false);
      setShowDetailExportMenu(false);
    }
  };

  const exportDetailExcel = async () => {
    setIsExportingDetails(true);
    try {
      const rows = await fetchDetailRows();
      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet('Case Details');
      ws.addRow(detailHeaders);
      const headerRow = ws.getRow(1);
      headerRow.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF4F46E5' },
      };
      headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      headerRow.alignment = { horizontal: 'center' };
      rows.forEach((r) => ws.addRow(detailRowToArray(r)));
      ws.columns.forEach((col, i) => {
        const maxLen = Math.max(
          detailHeaders[i].length,
          ...rows.map((r) => String(detailRowToArray(r)[i]).length)
        );
        col.width = Math.min(maxLen + 4, 40);
      });

      const buffer = await wb.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `executive_summary_details_${new Date().toISOString().split('T')[0]}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      alert(`Export failed: ${err.message}`);
    } finally {
      setIsExportingDetails(false);
      setShowDetailExportMenu(false);
    }
  };

  const exportDetailPDF = async () => {
    setIsExportingDetails(true);
    try {
      const rows = await fetchDetailRows();
      const doc = new jsPDF({ orientation: 'landscape' });
      doc.setFontSize(16);
      doc.text('Executive Summary — Case Details', 14, 18);
      doc.setFontSize(9);
      doc.setTextColor(120);
      const filterText = [
        startDate && `From: ${startDate}`,
        endDate && `To: ${endDate}`,
      ]
        .filter(Boolean)
        .join('  |  ');
      doc.text(filterText || 'All dates', 14, 25);

      autoTable(doc, {
        startY: 32,
        head: [detailHeaders],
        body: rows.map(detailRowToArray),
        styles: { fontSize: 7, cellPadding: 2 },
        headStyles: { fillColor: [79, 70, 229], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [248, 248, 252] },
      });

      doc.save(
        `executive_summary_details_${new Date().toISOString().split('T')[0]}.pdf`
      );
    } catch (err: any) {
      alert(`Export failed: ${err.message}`);
    } finally {
      setIsExportingDetails(false);
      setShowDetailExportMenu(false);
    }
  };

  const showingFrom = pageSize === 0 ? 1 : currentPage * pageSize + 1;
  const showingTo =
    pageSize === 0 ? totalDays : Math.min((currentPage + 1) * pageSize, totalDays);

  return (
    <div className="flex-1 bg-neutral-50 overflow-auto">
      {/* Header */}
      <div className="bg-white border-b border-neutral-200 px-8 py-6">
        <div className="flex items-center space-x-4">
          <div className="p-2.5 bg-indigo-100 rounded-xl">
            <BarChart3 className="w-6 h-6 text-indigo-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">Reports</h1>
            <p className="text-neutral-500 text-sm mt-0.5">
              Payroll case processing metrics and reports
            </p>
          </div>
        </div>

        {/* Tab bar */}
        <div className="flex space-x-6 mt-6 border-b border-neutral-200 -mb-6">
          <button
            onClick={() => setActiveTab('executive_summary')}
            className={`pb-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'executive_summary'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-neutral-500 hover:text-neutral-700'
            }`}
          >
            Executive Summary
          </button>
          <button
            onClick={() => setActiveTab('manual_reasons')}
            className={`pb-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'manual_reasons'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-neutral-500 hover:text-neutral-700'
            }`}
          >
            Manual Processing Reasons
          </button>
        </div>
      </div>

      {activeTab === 'executive_summary' && (
        <div className="p-8 space-y-6">
          {/* Filters + Export */}
          <div className="bg-white rounded-xl shadow-sm border border-neutral-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <Filter size={16} className="text-neutral-500" />
                <h3 className="text-sm font-semibold text-neutral-700 uppercase tracking-wide">
                  Filters
                </h3>
              </div>
              <div className="flex items-center space-x-2">
                {/* Export Summary dropdown */}
                <div className="relative">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowExportMenu((prev) => !prev);
                      setShowDetailExportMenu(false);
                    }}
                    disabled={data.length === 0}
                    className="flex items-center space-x-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <Download size={16} />
                    <span>Export</span>
                  </button>
                  {showExportMenu && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-neutral-200 py-1 z-20">
                      <button
                        onClick={exportCSV}
                        className="w-full flex items-center space-x-3 px-4 py-2.5 text-sm text-neutral-700 hover:bg-neutral-50 transition-colors"
                      >
                        <FileText size={16} className="text-green-600" />
                        <span>Export as CSV</span>
                      </button>
                      <button
                        onClick={exportExcel}
                        className="w-full flex items-center space-x-3 px-4 py-2.5 text-sm text-neutral-700 hover:bg-neutral-50 transition-colors"
                      >
                        <FileSpreadsheet size={16} className="text-emerald-600" />
                        <span>Export as Excel</span>
                      </button>
                      <button
                        onClick={exportPDF}
                        className="w-full flex items-center space-x-3 px-4 py-2.5 text-sm text-neutral-700 hover:bg-neutral-50 transition-colors"
                      >
                        <FileText size={16} className="text-red-600" />
                        <span>Export as PDF</span>
                      </button>
                    </div>
                  )}
                </div>

                {/* Export Details dropdown */}
                <div className="relative">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowDetailExportMenu((prev) => !prev);
                      setShowExportMenu(false);
                    }}
                    disabled={data.length === 0 || isExportingDetails}
                    className="flex items-center space-x-2 px-4 py-2 border border-indigo-600 text-indigo-600 text-sm font-medium rounded-lg hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ClipboardList size={16} />
                    <span>
                      {isExportingDetails ? 'Exporting...' : 'Export Details'}
                    </span>
                  </button>
                  {showDetailExportMenu && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-neutral-200 py-1 z-20">
                      <button
                        onClick={exportDetailCSV}
                        className="w-full flex items-center space-x-3 px-4 py-2.5 text-sm text-neutral-700 hover:bg-neutral-50 transition-colors"
                      >
                        <FileText size={16} className="text-green-600" />
                        <span>Details as CSV</span>
                      </button>
                      <button
                        onClick={exportDetailExcel}
                        className="w-full flex items-center space-x-3 px-4 py-2.5 text-sm text-neutral-700 hover:bg-neutral-50 transition-colors"
                      >
                        <FileSpreadsheet size={16} className="text-emerald-600" />
                        <span>Details as Excel</span>
                      </button>
                      <button
                        onClick={exportDetailPDF}
                        className="w-full flex items-center space-x-3 px-4 py-2.5 text-sm text-neutral-700 hover:bg-neutral-50 transition-colors"
                      >
                        <FileText size={16} className="text-red-600" />
                        <span>Details as PDF</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="flex flex-wrap items-end gap-4">
              <div>
                <label className="block text-xs font-medium text-neutral-500 mb-1">
                  Start Date
                </label>
                <div className="relative">
                  <Calendar
                    size={14}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"
                  />
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="pl-9 pr-3 py-2 border border-neutral-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-neutral-500 mb-1">
                  End Date
                </label>
                <div className="relative">
                  <Calendar
                    size={14}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"
                  />
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="pl-9 pr-3 py-2 border border-neutral-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                  />
                </div>
              </div>
              {(startDate || endDate) && (
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 text-neutral-600 text-sm font-medium bg-neutral-100 rounded-lg hover:bg-neutral-200 transition-colors"
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Table */}
          <div className="bg-white rounded-xl shadow-sm border border-neutral-200 overflow-hidden">
            {error ? (
              <div className="p-12 text-center">
                <p className="text-red-600 font-medium">{error}</p>
                <button
                  onClick={fetchData}
                  className="mt-4 px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  Retry
                </button>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full table-fixed">
                    <thead>
                      <tr className="bg-neutral-100 border-b border-neutral-200">
                        <th className="w-48 text-center px-4 py-3.5">
                          <button
                            onClick={toggleSort}
                            className="inline-flex items-center space-x-1.5 text-sm font-semibold text-neutral-600 uppercase tracking-wide hover:text-neutral-900 transition-colors"
                          >
                            <span>Date</span>
                            {sortOrder === 'desc' ? (
                              <ArrowDown size={14} />
                            ) : (
                              <ArrowUp size={14} />
                            )}
                          </button>
                        </th>
                        <th className="text-center px-4 py-3.5 text-sm font-semibold text-neutral-600 uppercase tracking-wide">
                          Total Pulled
                        </th>
                        <th className="hidden text-center px-4 py-3.5 text-sm font-semibold text-neutral-600 uppercase tracking-wide">
                          Qualified
                        </th>
                        <th className="text-center px-4 py-3.5 text-sm font-semibold text-neutral-600 uppercase tracking-wide">
                          Processed
                        </th>
                        <th className="hidden text-center px-4 py-3.5 text-sm font-semibold text-neutral-600 uppercase tracking-wide">
                          % Processed / Qualified
                        </th>
                        <th className="text-center px-4 py-3.5 text-sm font-semibold text-neutral-600 uppercase tracking-wide">
                          % Processed / Total
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {isLoading ? (
                        <tr>
                          <td colSpan={6} className="px-4 py-16 text-center">
                            <div className="flex flex-col items-center space-y-3">
                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                              <span className="text-sm text-neutral-500">
                                Loading...
                              </span>
                            </div>
                          </td>
                        </tr>
                      ) : data.length === 0 ? (
                        <tr>
                          <td
                            colSpan={6}
                            className="px-4 py-16 text-center text-neutral-500"
                          >
                            No data found for the selected date range.
                          </td>
                        </tr>
                      ) : (
                        data.map((row, idx) => (
                          <tr
                            key={row.date}
                            className={`border-b border-neutral-100 transition-colors ${
                              idx % 2 === 0 ? 'bg-white' : 'bg-neutral-50/70'
                            } hover:bg-indigo-50/40`}
                          >
                            <td className="px-4 py-4 text-base font-medium text-neutral-900 text-center">
                              {formatDate(row.date)}
                            </td>
                            <td className="px-4 py-4 text-base text-neutral-700 text-center tabular-nums font-medium">
                              {row.total_pulled.toLocaleString()}
                            </td>
                            <td className="hidden px-4 py-4 text-base text-neutral-700 text-center tabular-nums font-medium">
                              {row.qualified.toLocaleString()}
                            </td>
                            <td className="px-4 py-4 text-base text-neutral-700 text-center tabular-nums font-medium">
                              {row.processed.toLocaleString()}
                            </td>
                            <td className="hidden px-4 py-4 text-center">
                              <span
                                className={`inline-block px-3 py-1 rounded-full text-sm font-semibold tabular-nums ${getPctColor(row.pct_processed_qualified)}`}
                              >
                                {formatPct(row.pct_processed_qualified)}
                              </span>
                            </td>
                            <td className="px-4 py-4 text-center">
                              <span
                                className={`inline-block px-3 py-1 rounded-full text-sm font-semibold tabular-nums ${getPctColor(row.pct_processed_total)}`}
                              >
                                {formatPct(row.pct_processed_total)}
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination bar — always visible */}
                <div className="flex items-center justify-between px-6 py-4 border-t border-neutral-200 bg-neutral-50/50">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <label className="text-sm text-neutral-500">Show</label>
                      <select
                        value={pageSize}
                        onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                        className="border border-neutral-300 rounded-lg px-2 py-1.5 text-sm text-neutral-700 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                      >
                        {PAGE_SIZE_OPTIONS.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt === 0 ? 'All' : opt}
                          </option>
                        ))}
                      </select>
                      <span className="text-sm text-neutral-500">entries</span>
                    </div>
                    <span className="text-sm text-neutral-400">|</span>
                    <p className="text-sm text-neutral-600">
                      Showing{' '}
                      <span className="font-semibold text-neutral-800">
                        {totalDays === 0 ? 0 : showingFrom}
                      </span>
                      –
                      <span className="font-semibold text-neutral-800">
                        {showingTo}
                      </span>{' '}
                      of{' '}
                      <span className="font-semibold text-neutral-800">
                        {totalDays}
                      </span>{' '}
                      total days
                    </p>
                  </div>

                  {pageSize > 0 && totalPages > 1 && (
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                        disabled={currentPage === 0}
                        className="p-2 rounded-lg border border-neutral-300 text-neutral-600 hover:bg-neutral-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      >
                        <ChevronLeft size={16} />
                      </button>
                      <span className="text-sm text-neutral-600 px-2">
                        Page {currentPage + 1} of {totalPages}
                      </span>
                      <button
                        onClick={() =>
                          setCurrentPage((p) => Math.min(totalPages - 1, p + 1))
                        }
                        disabled={currentPage >= totalPages - 1}
                        className="p-2 rounded-lg border border-neutral-300 text-neutral-600 hover:bg-neutral-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      >
                        <ChevronRight size={16} />
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {activeTab === 'manual_reasons' && (
        <div className="p-8 space-y-6">
          {/* Reason Summary Cards */}
          {reasonSummary.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {reasonSummary.map((r) => (
                <div
                  key={r.reason}
                  className="bg-white rounded-xl shadow-sm border border-neutral-200 p-4"
                >
                  <p
                    className="text-xs font-semibold text-neutral-500 uppercase tracking-wide truncate"
                    title={r.reason}
                  >
                    {r.reason.replace(/_/g, ' ')}
                  </p>
                  <div className="mt-2 flex items-baseline space-x-2">
                    <span className="text-2xl font-bold text-neutral-900">
                      {r.count.toLocaleString()}
                    </span>
                    <span className="text-sm font-semibold text-indigo-600">
                      {r.percentage}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Filters + Search + Export */}
          <div className="bg-white rounded-xl shadow-sm border border-neutral-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <Filter size={16} className="text-neutral-500" />
                <h3 className="text-sm font-semibold text-neutral-700 uppercase tracking-wide">
                  Filters
                </h3>
              </div>
              <div className="relative">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setManualShowExportMenu((prev) => !prev);
                  }}
                  disabled={manualData.length === 0}
                  className="flex items-center space-x-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <Download size={16} />
                  <span>Export</span>
                </button>
                {manualShowExportMenu && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-neutral-200 py-1 z-20">
                    <button
                      onClick={exportManualCSV}
                      className="w-full flex items-center space-x-3 px-4 py-2.5 text-sm text-neutral-700 hover:bg-neutral-50 transition-colors"
                    >
                      <FileText size={16} className="text-green-600" />
                      <span>Export as CSV</span>
                    </button>
                    <button
                      onClick={exportManualExcel}
                      className="w-full flex items-center space-x-3 px-4 py-2.5 text-sm text-neutral-700 hover:bg-neutral-50 transition-colors"
                    >
                      <FileSpreadsheet size={16} className="text-emerald-600" />
                      <span>Export as Excel</span>
                    </button>
                    <button
                      onClick={exportManualPDF}
                      className="w-full flex items-center space-x-3 px-4 py-2.5 text-sm text-neutral-700 hover:bg-neutral-50 transition-colors"
                    >
                      <FileText size={16} className="text-red-600" />
                      <span>Export as PDF</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex-1 min-w-[200px]">
                <label className="block text-xs font-medium text-neutral-500 mb-1">
                  Search
                </label>
                <div className="relative">
                  <Search
                    size={14}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"
                  />
                  <input
                    type="text"
                    placeholder="Case number, reason, comment, or user..."
                    value={manualSearch}
                    onChange={(e) => {
                      setManualSearch(e.target.value);
                      setManualCurrentPage(0);
                    }}
                    className="w-full pl-9 pr-3 py-2 border border-neutral-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-neutral-500 mb-1">
                  Start Date
                </label>
                <div className="relative">
                  <Calendar
                    size={14}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"
                  />
                  <input
                    type="date"
                    value={manualStartDate}
                    onChange={(e) => {
                      setManualStartDate(e.target.value);
                      setManualCurrentPage(0);
                    }}
                    className="pl-9 pr-3 py-2 border border-neutral-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-neutral-500 mb-1">
                  End Date
                </label>
                <div className="relative">
                  <Calendar
                    size={14}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"
                  />
                  <input
                    type="date"
                    value={manualEndDate}
                    onChange={(e) => {
                      setManualEndDate(e.target.value);
                      setManualCurrentPage(0);
                    }}
                    className="pl-9 pr-3 py-2 border border-neutral-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                  />
                </div>
              </div>
              {(manualStartDate || manualEndDate || manualSearch) && (
                <button
                  onClick={manualClearFilters}
                  className="px-4 py-2 text-neutral-600 text-sm font-medium bg-neutral-100 rounded-lg hover:bg-neutral-200 transition-colors"
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Detail Table */}
          <div className="bg-white rounded-xl shadow-sm border border-neutral-200 overflow-hidden">
            {manualError ? (
              <div className="p-12 text-center">
                <p className="text-red-600 font-medium">{manualError}</p>
                <button
                  onClick={fetchManualData}
                  className="mt-4 px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  Retry
                </button>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full table-fixed">
                    <thead>
                      <tr className="bg-neutral-100 border-b border-neutral-200">
                        <th className="w-36 text-center px-4 py-3.5">
                          <span className="text-sm font-semibold text-neutral-600 uppercase tracking-wide">
                            Case Number
                          </span>
                        </th>
                        <th className="w-40 text-center px-4 py-3.5">
                          <span className="text-sm font-semibold text-neutral-600 uppercase tracking-wide">
                            Reason
                          </span>
                        </th>
                        <th className="text-center px-4 py-3.5">
                          <span className="text-sm font-semibold text-neutral-600 uppercase tracking-wide">
                            Comment
                          </span>
                        </th>
                        <th className="w-44 text-center px-4 py-3.5">
                          <span className="text-sm font-semibold text-neutral-600 uppercase tracking-wide">
                            Processed By
                          </span>
                        </th>
                        <th className="w-44 text-center px-4 py-3.5">
                          <button
                            onClick={manualToggleSort}
                            className="inline-flex items-center space-x-1.5 text-sm font-semibold text-neutral-600 uppercase tracking-wide hover:text-neutral-900 transition-colors"
                          >
                            <span>Date</span>
                            {manualSortOrder === 'desc' ? (
                              <ArrowDown size={14} />
                            ) : (
                              <ArrowUp size={14} />
                            )}
                          </button>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {manualLoading ? (
                        <tr>
                          <td colSpan={5} className="px-4 py-16 text-center">
                            <div className="flex flex-col items-center space-y-3">
                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                              <span className="text-sm text-neutral-500">
                                Loading...
                              </span>
                            </div>
                          </td>
                        </tr>
                      ) : manualData.length === 0 ? (
                        <tr>
                          <td
                            colSpan={5}
                            className="px-4 py-16 text-center text-neutral-500"
                          >
                            No manual processing logs found.
                          </td>
                        </tr>
                      ) : (
                        manualData.map((row, idx) => (
                          <tr
                            key={`${row.case_number}-${row.created_at}-${idx}`}
                            className={`border-b border-neutral-100 transition-colors ${
                              idx % 2 === 0 ? 'bg-white' : 'bg-neutral-50/70'
                            } hover:bg-indigo-50/40`}
                          >
                            <td className="px-4 py-4 text-sm font-medium text-indigo-600 text-center">
                              {row.case_number}
                            </td>
                            <td className="px-4 py-4 text-sm text-neutral-700 text-center">
                              <span
                                className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold border ${getReasonColor(row.reason)}`}
                              >
                                {row.reason.replace(/_/g, ' ')}
                              </span>
                            </td>
                            <td className="px-4 py-4 text-sm text-neutral-600 text-center">
                              <span
                                className="block max-w-xs mx-auto truncate cursor-default"
                                title={row.comment || ''}
                              >
                                {row.comment || '—'}
                              </span>
                            </td>
                            <td
                              className="px-4 py-4 text-sm text-neutral-700 text-center"
                              title={row.processed_by}
                            >
                              {formatProcessedBy(row.processed_by)}
                            </td>
                            <td className="px-4 py-4 text-sm font-medium text-neutral-900 text-center">
                              {row.created_at ? formatDate(row.created_at) : '—'}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between px-6 py-4 border-t border-neutral-200 bg-neutral-50/50">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <label className="text-sm text-neutral-500">Show</label>
                      <select
                        value={manualPageSize}
                        onChange={(e) =>
                          manualHandlePageSizeChange(Number(e.target.value))
                        }
                        className="border border-neutral-300 rounded-lg px-2 py-1.5 text-sm text-neutral-700 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                      >
                        {PAGE_SIZE_OPTIONS.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt === 0 ? 'All' : opt}
                          </option>
                        ))}
                      </select>
                      <span className="text-sm text-neutral-500">entries</span>
                    </div>
                    <span className="text-sm text-neutral-400">|</span>
                    <p className="text-sm text-neutral-600">
                      Showing{' '}
                      <span className="font-semibold text-neutral-800">
                        {manualTotalRows === 0 ? 0 : manualShowingFrom}
                      </span>
                      –
                      <span className="font-semibold text-neutral-800">
                        {manualShowingTo}
                      </span>{' '}
                      of{' '}
                      <span className="font-semibold text-neutral-800">
                        {manualTotalRows}
                      </span>{' '}
                      total entries
                    </p>
                  </div>

                  {manualPageSize > 0 && manualTotalPages > 1 && (
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setManualCurrentPage((p) => Math.max(0, p - 1))}
                        disabled={manualCurrentPage === 0}
                        className="p-2 rounded-lg border border-neutral-300 text-neutral-600 hover:bg-neutral-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      >
                        <ChevronLeft size={16} />
                      </button>
                      <span className="text-sm text-neutral-600 px-2">
                        Page {manualCurrentPage + 1} of {manualTotalPages}
                      </span>
                      <button
                        onClick={() =>
                          setManualCurrentPage((p) =>
                            Math.min(manualTotalPages - 1, p + 1)
                          )
                        }
                        disabled={manualCurrentPage >= manualTotalPages - 1}
                        className="p-2 rounded-lg border border-neutral-300 text-neutral-600 hover:bg-neutral-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      >
                        <ChevronRight size={16} />
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ExecutiveSummary;
